import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { createApp } from 'vue';
import PDFTemplate from '@/components/PDFTemplate.vue';
import reportsService from '@/service/reportsService';

class HTMLToPDFService {
    constructor() {
        this.tempContainer = null;
    }

    getTabTitles(tabIndex, period = 'week') {
        const periodText = this.getPeriodText(period);

        const titleMappings = {
            0: {
                main: `${periodText} Testler Genel Değerlendirme Raporu`,
                charts: {
                    userChart: "Atanan Kullanıcı Sayısı",
                    genderChart: "Cinsiyet",
                    rolesChart: "Kullanıcı Rolleri",
                    departmentsChart: "Departmanlar"
                }
            },
            1: {
                main: `${periodText} Programlar Genel Değerlendirme Raporu`,
                charts: {
                    userChart: "Atanan Kullanıcı Sayısı",
                    genderChart: "Cinsiyet",
                    rolesChart: "Kullanıcı Rolleri",
                    departmentsChart: "Departmanlar"
                }
            },
            2: {
                main: `${periodText} Kullanıcılar Genel Değerlendirme Raporu`,
                charts: {
                    userChart: "Atanan Kullanıcı Sayısı",
                    genderChart: "Cinsiyet",
                    rolesChart: "Kullanıcı Rolleri",
                    departmentsChart: "Departmanlar"
                }
            },
            3: {
                main: `${periodText} GameLabs Genel Değerlendirme Raporu`,
                charts: {
                    userChart: "Atanan Kullanıcı Sayısı",
                    genderChart: "Cinsiyet",
                    rolesChart: "Kullanıcı Rolleri",
                    departmentsChart: "Departmanlar"
                }
            }
        };

        return titleMappings[tabIndex] || titleMappings[0];
    }

    getPeriodText(period) {
        const periodMapping = {
            'week': '1 Haftalık',
            'month': '1 Aylık',
            'year': '1 Yıllık'
        };

        return periodMapping[period] || '1 Haftalık';
    }

    formatDateRange(startDate, endDate) {
        if (!startDate && !endDate) {
            return new Date().toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        const formatDate = (date) => {
            return new Date(date).toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        };

        if (startDate && endDate) {
            return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        } else if (startDate) {
            return `${formatDate(startDate)} tarihinden itibaren`;
        } else {
            return `${formatDate(endDate)} tarihine kadar`;
        }
    }

    getDateRangeForPeriod(period = 'week', startDate = null, endDate = null) {
        if (startDate && endDate) {
            const formatDate = (date) => {
                return new Date(date).toLocaleDateString('tr-TR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            };
            return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        }

        const today = new Date();
        let startPeriod;

        switch (period) {
            case 'week':
                startPeriod = new Date(today);
                startPeriod.setDate(today.getDate() - 7);
                break;
            case 'month':
                startPeriod = new Date(today);
                startPeriod.setMonth(today.getMonth() - 1);
                break;
            case 'year':
                startPeriod = new Date(today);
                startPeriod.setFullYear(today.getFullYear() - 1);
                break;
            default:
                startPeriod = new Date(today);
                startPeriod.setDate(today.getDate() - 7);
        }

        const formatDate = (date) => {
            return date.toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        };

        return `${formatDate(startPeriod)} - ${formatDate(today)}`;
    }

    async getChartImagesFromDOM() {
        const chartImages = [];

        const chartElements = [
            { id: 'userLineChart', key: 'userChart' },
            { id: 'genderPieChart', key: 'genderChart' },
            { id: 'userRolesChart', key: 'rolesChart' },
            { id: 'departmentDonutChart', key: 'departmentsChart' }
        ];

        for (const chartInfo of chartElements) {
            const element = document.getElementById(chartInfo.id);
            if (element) {
                try {
                    const rect = element.getBoundingClientRect();
                    if (rect.width === 0 || rect.height === 0) {
                        continue;
                    }

                    const canvas = await html2canvas(element, {
                        scale: 4,
                        useCORS: true,
                        allowTaint: true,
                        backgroundColor: '#ffffff',
                        width: Math.max(rect.width, 800),
                        height: Math.max(rect.height, 400),
                        scrollX: 0,
                        scrollY: 0,
                        logging: false,
                        imageTimeout: 15000,
                        removeContainer: true,
                        foreignObjectRendering: true,
                        onclone: function(clonedDoc) {
                            const clonedElement = clonedDoc.getElementById(chartInfo.id);
                            if (clonedElement) {
                                clonedElement.style.width = rect.width + 'px';
                                clonedElement.style.height = rect.height + 'px';
                            }
                        }
                    });

                    const dataURI = canvas.toDataURL('image/png', 1.0);

                    chartImages.push({
                        key: chartInfo.key,
                        image: dataURI,
                        title: ''
                    });
                } catch (error) {
                }
            }
        }

        return chartImages;
    }

    async getChartImages(chartRefs) {
        const chartImages = [];

        for (const [chartKey, chartInstance] of Object.entries(chartRefs)) {
            if (!chartInstance) continue;

            try {
                let dataURI = null;

                try {
                    if (chartInstance.exports && typeof chartInstance.exports.exportToSVG === 'function') {
                        const svgString = await chartInstance.exports.exportToSVG();
                        if (svgString) {
                            dataURI = await this.svgToHighQualityPNG(svgString);
                        }
                    }
                } catch (svgError) {
                }

                if (!dataURI) {
                    try {
                        if (typeof chartInstance.dataURI === 'function') {
                            const result = await chartInstance.dataURI({
                                type: 'png',
                                width: 800,
                                height: 400,
                                quality: 1
                            });
                            dataURI = result.imgURI || result;
                        }
                    } catch (dataURIError) {
                    }
                }

                if (!dataURI) {
                    try {
                        dataURI = await chartInstance.dataURI();
                    } catch (simpleError) {
                    }
                }

                if (!dataURI) {
                    try {
                        await new Promise(resolve => setTimeout(resolve, 500));
                        dataURI = await chartInstance.dataURI();
                    } catch (delayedError) {
                    }
                }

                if (dataURI) {
                    chartImages.push({
                        key: chartKey,
                        image: dataURI,
                        title: ''
                    });
                }

            } catch (error) {
            }
        }

        return chartImages;
    }

    async svgToHighQualityPNG(svgString) {
        return new Promise((resolve, reject) => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                canvas.width = 1200;
                canvas.height = 600;

                img.onload = function() {
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    const dataURI = canvas.toDataURL('image/png', 1.0);
                    URL.revokeObjectURL(url);
                    resolve(dataURI);
                };

                img.onerror = function(error) {
                    reject(error);
                };

                const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(svgBlob);
                img.src = url;

            } catch (error) {
                reject(error);
            }
        });
    }

    async mountTemplate(templateData) {
        try {
            this.tempContainer = document.createElement('div');
            this.tempContainer.style.position = 'absolute';
            this.tempContainer.style.left = '-9999px';
            this.tempContainer.style.top = '-9999px';
            this.tempContainer.style.width = '210mm';
            this.tempContainer.style.height = 'auto';
            document.body.appendChild(this.tempContainer);

            const app = createApp(PDFTemplate, templateData);
            const instance = app.mount(this.tempContainer);

            await this.$nextTick();

            const images = this.tempContainer.querySelectorAll('img');
            for (let index = 0; index < images.length; index++) {
                const img = images[index];
                if (img.src && img.src.startsWith('data:image')) {
                    await new Promise((resolve) => {
                        if (img.complete) {
                            resolve();
                        } else {
                            img.onload = resolve;
                            img.onerror = resolve;
                        }
                    });
                }
            }

            return this.tempContainer;

        } catch (error) {
            throw error;
        }
    }

    cleanupTemplate() {
        if (this.tempContainer && this.tempContainer.parentNode) {
            this.tempContainer.parentNode.removeChild(this.tempContainer);
            this.tempContainer = null;
        }
    }

    base64ToBlob(base64Data) {
        const byteCharacters = atob(base64Data.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: 'image/png' });
    }

    base64ToCanvas(base64Data) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = function() {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;

                ctx.drawImage(img, 0, 0);
                resolve(canvas);
            };
            img.onerror = reject;
            img.src = base64Data;
        });
    }

    async getChartSVGs(chartRefs) {
        const chartSVGs = [];

        for (const [chartKey, chartInstance] of Object.entries(chartRefs)) {
            if (chartInstance) {
                try {
                    let svgString = null;

                    const chartElement = chartInstance.el;
                    if (chartElement) {
                        const svgElement = chartElement.querySelector('svg');
                        if (svgElement) {
                            const clonedSvg = svgElement.cloneNode(true);
                            clonedSvg.removeAttribute('id');
                            clonedSvg.removeAttribute('class');
                            svgString = clonedSvg.outerHTML;
                        }
                    }

                    if (svgString && svgString.length > 100) {
                        chartSVGs.push({
                            key: chartKey,
                            svg: svgString,
                            title: ''
                        });
                    }

                } catch (error) {
                }
            }
        }

        return chartSVGs;
    }

    getReportType(tabIndex) {
        const typeMapping = {
            0: 'selfAssessments',
            1: 'programs',
            2: 'users',
            3: 'gameLabs'
        };

        return typeMapping[tabIndex] || 'selfAssessments';
    }

    async generatePDF(chartRefs, tabIndex, dateFilters = {}, period = 'week', chartData = null) {
        try {
            const chartSVGs = await this.getChartSVGs(chartRefs);

            if (!chartSVGs || chartSVGs.length === 0) {
                const chartImages = await this.getChartImagesFromDOM();
                return await this.generatePDFWithCanvas(chartImages, tabIndex, dateFilters, period, chartData);
            }

            const tabTitles = this.getTabTitles(tabIndex, period);

            const chartsWithTitles = chartSVGs.map(chart => ({
                ...chart,
                title: tabTitles.charts[chart.key] || chart.key,
                dateRange: this.getDateRangeForPeriod(period, dateFilters.startDate, dateFilters.endDate)
            }));

            const reportDescription = chartData ? this.generateReportDescription(tabIndex, period, chartData) : '';

            const templateData = {
                reportTitle: tabTitles.main,
                dateRange: this.getDateRangeForPeriod(period, dateFilters.startDate, dateFilters.endDate),
                charts: chartsWithTitles,
                reportDescription: reportDescription
            };

            const templateInstance = await this.mountTemplate(templateData);

            const chartContents = this.tempContainer.querySelectorAll('.chart-content');
            chartsWithTitles.forEach((chart, index) => {
                if (chartContents[index] && chart.svg) {
                    chartContents[index].innerHTML = chart.svg;

                    const svgElement = chartContents[index].querySelector('svg');
                    if (svgElement) {
                        const originalWidth = svgElement.getAttribute('width') || 400;
                        const originalHeight = svgElement.getAttribute('height') || 300;

                        if (!svgElement.getAttribute('viewBox')) {
                            svgElement.setAttribute('viewBox', `0 0 ${originalWidth} ${originalHeight}`);
                        }
                        svgElement.setAttribute('preserveAspectRatio', 'xMinYMin meet');

                        svgElement.removeAttribute('width');
                        svgElement.removeAttribute('height');

                        svgElement.style.width = '100%';
                        svgElement.style.height = '100%';
                        svgElement.style.maxWidth = '100%';
                        svgElement.style.maxHeight = '100%';
                        svgElement.style.minWidth = '100%';
                        svgElement.style.minHeight = '100%';
                    }
                }
            });

            await new Promise(resolve => setTimeout(resolve, 1500));

            const canvas = await html2canvas(this.tempContainer, {
                scale: 3,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                width: 794,
                height: 1123,
                scrollX: 0,
                scrollY: 0,
                windowWidth: 794,
                windowHeight: 1123,
                logging: false,
                ignoreElements: (element) => {
                    return element.classList && element.classList.contains('chart-action-btn');
                }
            });

            const imgData = canvas.toDataURL('image/png');

            const templateWidth = 210;
            const templateHeight = Math.min(297, (canvas.height * templateWidth) / canvas.width);

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [templateWidth, templateHeight]
            });

            pdf.setFont("helvetica");

            pdf.addImage(imgData, 'PNG', 0, 0, templateWidth, templateHeight);

            const fileName = `${tabTitles.main.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;

            const pdfBlob = pdf.output('blob');

            pdf.save(fileName);

            try {
                const reportType = this.getReportType(tabIndex);
                await reportsService.createSavedReport(pdfBlob, fileName, reportType);
                console.log('PDF başarıyla backend\'e kaydedildi');
            } catch (saveError) {
                console.error('PDF backend\'e kaydedilirken hata oluştu:', saveError);
            }

            this.cleanupTemplate();

            return true;

        } catch (error) {
            this.cleanupTemplate();
            throw error;
        }
    }

    async generatePDFWithCanvas(chartsWithCanvas, tabIndex, dateFilters, period = 'week', chartData = null) {
        try {
            const tabTitles = this.getTabTitles(tabIndex, period);

            const chartsWithTitles = chartsWithCanvas.map(chart => ({
                ...chart,
                title: tabTitles.charts[chart.key] || chart.key
            }));

            const reportDescription = chartData ? this.generateReportDescription(tabIndex, period, chartData) : '';

            const templateData = {
                reportTitle: tabTitles.main,
                dateRange: this.getDateRangeForPeriod(period, dateFilters.startDate, dateFilters.endDate),
                charts: chartsWithTitles,
                reportDescription: reportDescription
            };

            const chartContents = this.tempContainer.querySelectorAll('.chart-content');
            chartsWithTitles.forEach((chart, index) => {
                if (chartContents[index] && chart.canvas) {
                    chartContents[index].appendChild(chart.canvas);
                }
            });

            await new Promise(resolve => setTimeout(resolve, 1000));

            const canvas = await html2canvas(this.tempContainer, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                width: 794,
                height: 1123,
                scrollX: 0,
                scrollY: 0,
                windowWidth: 794,
                windowHeight: 1123,
                logging: false
            });

            const imgData = canvas.toDataURL('image/png');
            const pdfWidth = 210;
            const pdfHeight = 297;

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [pdfWidth, pdfHeight]
            });

            pdf.setFont("helvetica");

            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

            const fileName = `${tabTitles.main.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;

            const pdfBlob = pdf.output('blob');

            pdf.save(fileName);

            try {
                const reportType = this.getReportType(tabIndex);
                await reportsService.createSavedReport(pdfBlob, fileName, reportType);
                console.log('PDF başarıyla backend\'e kaydedildi');
            } catch (saveError) {
                console.error('PDF backend\'e kaydedilirken hata oluştu:', saveError);
            }

            this.cleanupTemplate();

            return true;

        } catch (error) {
            this.cleanupTemplate();
            throw error;
        }
    }

    $nextTick() {
        return new Promise(resolve => {
            setTimeout(resolve, 100);
        });
    }

    generateReportDescription(tabIndex, period, chartData) {
        const periodText = this.getPeriodText(period).toLowerCase();
        const analysisData = this.performDetailedAnalysis(chartData, tabIndex);

        const baseDescriptions = {
            0: {
                intro: `İlgili ${periodText} boyunca yapılan test atamalarına dair veriler, eğitim ve test modüllerine yönelik eğilimlerin zaman içerisinde nasıl değiştiğini ortaya koymaktadır.${analysisData.hasUserData ? ` Test kullanımında ${analysisData.trendDescription} gözlemlenmiştir.` : ''}`,
                details: `${analysisData.testAnalysis}${analysisData.hasMultipleTests ? ` Farklı test türleri arasında kullanım oranları değişkenlik göstermiştir.` : ''}`,
                userAnalysis: `${analysisData.userProfileAnalysis}${analysisData.hasRoleData ? ` Rol bazlı dağılımda en yüksek kullanıcı sayısının ${analysisData.topRole} oluştuğu görülmektedir (${analysisData.topRoleCount} kişi), bu da sistemin öncelikli olarak bu grup tarafından kullanıldığını göstermektedir.` : ''}`,
                departmentAnalysis: `${analysisData.departmentAnalysis}${analysisData.hasDepartmentData ? ` "${analysisData.topDepartment}" en yüksek kullanıcı katılımına sahiptir (${analysisData.topDepartmentCount} kişi) ve bu departman, platformun kullanım yaygınlığı açısından öne çıkmaktadır.` : ''}`,
                conclusion: "Bu veriler, eğitim ve gelişim içeriklerinin dağılımı planlanırken rol ve departman bazlı hedeflemelerin önemine dikkat çekmektedir."
            },
            1: {
                intro: `İlgili ${periodText} boyunca yapılan program atamalarına dair veriler, eğitim programlarına yönelik eğilimlerin zaman içerisinde nasıl değiştiğini ortaya koymaktadır.${analysisData.hasUserData ? ` Program katılımları ${analysisData.trendDescription} göstermiştir.` : ''}`,
                details: `${analysisData.programAnalysis}${analysisData.hasMultiplePrograms ? ` Farklı program türleri arasında katılım oranları çeşitlilik göstermiştir.` : ''}`,
                userAnalysis: `${analysisData.userProfileAnalysis}${analysisData.hasRoleData ? ` Rol bazlı dağılımda ${analysisData.topRole} en aktif katılımcı grubu olmuştur (${analysisData.topRoleCount} kişi).` : ''}`,
                departmentAnalysis: `${analysisData.departmentAnalysis}${analysisData.hasDepartmentData ? ` "${analysisData.topDepartment}" departmanı ${analysisData.topDepartmentCount} kişi ile en yüksek katılımı göstermiştir.` : ''}`,
                conclusion: "Bu veriler, program içeriklerinin departman ve rol bazlı ihtiyaçlara göre özelleştirilmesinin önemini vurgulamaktadır."
            },
            2: {
                intro: `İlgili ${periodText} boyunca kullanıcı aktivitelerine dair veriler, kullanıcı davranışlarının zaman içerisinde nasıl değiştiğini ortaya koymaktadır.${analysisData.hasUserData ? ` Kullanıcı etkileşiminde ${analysisData.trendDescription} gözlemlenmiştir.` : ''}`,
                details: `${analysisData.genderAnalysis}${analysisData.hasGenderData ? ` Kullanıcı profili analizine göre platformdaki kullanıcıların %${analysisData.malePercentage}'i erkek, %${analysisData.femalePercentage}'ı kadınlardan oluşmaktadır.` : ''}`,
                userAnalysis: `${analysisData.roleAnalysis}${analysisData.hasRoleData ? ` Rol bazlı dağılımda en yüksek kullanıcı sayısının ${analysisData.topRole} oluştuğu görülmektedir (${analysisData.topRoleCount} kişi).` : ''}`,
                departmentAnalysis: `${analysisData.departmentAnalysis}${analysisData.hasDepartmentData ? ` "${analysisData.topDepartment}" en yüksek kullanıcı katılımına sahiptir (${analysisData.topDepartmentCount} kişi) ve bu departman, platformun kullanım yaygınlığı açısından öne çıkmaktadır.` : ''}`,
                conclusion: "Bu durum, kullanıcı ihtiyaçlarının dönemsel olarak değişebileceğine ve kurumların içerik stratejilerini buna göre esnetmeleri gerektiğine işaret etmektedir."
            },
            3: {
                intro: `İlgili ${periodText} boyunca GameLab aktivitelerine dair veriler, oyun tabanlı öğrenme modüllerine yönelik eğilimlerin zaman içerisinde nasıl değiştiğini ortaya koymaktadır.${analysisData.hasUserData ? ` GameLab kullanımında ${analysisData.trendDescription} gözlemlenmiştir.` : ''}`,
                details: `${analysisData.gameLabAnalysis}${analysisData.hasMultipleGameLabs ? ` Farklı GameLab türleri arasında kullanım oranları değişkenlik göstermiştir.` : ''}`,
                userAnalysis: `${analysisData.userProfileAnalysis}${analysisData.hasRoleData ? ` Rol bazlı dağılımda ${analysisData.topRole} en aktif katılımcı grubu olmuştur (${analysisData.topRoleCount} kişi).` : ''}`,
                departmentAnalysis: `${analysisData.departmentAnalysis}${analysisData.hasDepartmentData ? ` "${analysisData.topDepartment}" departmanı ${analysisData.topDepartmentCount} kişi ile en yüksek katılımı göstermiştir.` : ''}`,
                conclusion: "Bu durum, kullanıcı ilgilerinin dönemsel olarak değişebileceğine ve kurumların GameLab stratejilerini buna göre esnetmeleri gerektiğine işaret etmektedir."
            }
        };

        const description = baseDescriptions[tabIndex] || baseDescriptions[2];
        return `${description.intro}\n${description.details}\n${description.userAnalysis}\n${description.departmentAnalysis}\n${description.conclusion}`;
    }

    performDetailedAnalysis(chartData, tabIndex) {
        const analysis = {
            hasUserData: false,
            hasGenderData: false,
            hasRoleData: false,
            hasDepartmentData: false,
            hasMultipleTests: false,
            hasMultiplePrograms: false,
            hasMultipleGameLabs: false,
            trendDescription: "dengeli bir seyir",
            testAnalysis: "Test kullanım verileri analiz edilmiştir.",
            programAnalysis: "Program katılım verileri analiz edilmiştir.",
            gameLabAnalysis: "GameLab aktivite verileri analiz edilmiştir.",
            genderAnalysis: "Kullanıcı demografik verileri incelenmiştir.",
            roleAnalysis: "Kullanıcı rol dağılımı analiz edilmiştir.",
            departmentAnalysis: "Departman bazlı kullanım verileri incelenmiştir.",
            userProfileAnalysis: "Kullanıcı profili verileri analiz edilmiştir.",
            malePercentage: "50",
            femalePercentage: "50",
            topRole: "kullanıcıların",
            topRoleCount: "0",
            topDepartment: "ana departmanın",
            topDepartmentCount: "0"
        };

        if (!chartData) {
            return analysis;
        }

        if (chartData.userChart?.series?.[0]?.data && chartData.userChart.series[0].data.length > 0) {
            analysis.hasUserData = true;
            const data = chartData.userChart.series[0].data;

            const firstHalf = data.slice(0, Math.floor(data.length / 2));
            const secondHalf = data.slice(Math.floor(data.length / 2));

            if (firstHalf.length > 0 && secondHalf.length > 0) {
                const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
                const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;

                if (secondAvg > firstAvg * 1.2) {
                    analysis.trendDescription = "dönem boyunca istikrarlı bir artış";
                } else if (secondAvg < firstAvg * 0.8) {
                    analysis.trendDescription = "dönem boyunca azalan bir eğilim";
                } else {
                    analysis.trendDescription = "dönem boyunca dengeli bir seyir";
                }
            }

            if (chartData.userChart.series.length > 1) {
                switch(tabIndex) {
                    case 0:
                        analysis.hasMultipleTests = true;
                        analysis.testAnalysis = "Farklı test türleri arasında kullanım dağılımı gözlemlenmiştir.";
                        break;
                    case 1:
                        analysis.hasMultiplePrograms = true;
                        analysis.programAnalysis = "Çeşitli program türleri arasında katılım oranları değişkenlik göstermiştir.";
                        break;
                    case 3:
                        analysis.hasMultipleGameLabs = true;
                        analysis.gameLabAnalysis = "Farklı GameLab türleri arasında kullanım oranları çeşitlilik göstermiştir.";
                        break;
                }
            }
        }

        if (chartData.genderChart?.series && chartData.genderChart.series.length >= 2) {
            const total = chartData.genderChart.series.reduce((a, b) => a + b, 0);
            if (total > 0) {
                analysis.hasGenderData = true;
                analysis.malePercentage = Math.round((chartData.genderChart.series[0] / total) * 100);
                analysis.femalePercentage = Math.round((chartData.genderChart.series[1] / total) * 100);

                if (analysis.malePercentage > 60) {
                    analysis.genderAnalysis = "Kullanıcı dağılımında erkek kullanıcılar daha aktif olmuştur.";
                } else if (analysis.malePercentage < 40) {
                    analysis.genderAnalysis = "Kullanıcı dağılımında kadın kullanıcılar daha aktif olmuştur.";
                } else {
                    analysis.genderAnalysis = "Kullanıcı dağılımında dengeli bir cinsiyet dağılımı gözlemlenmiştir.";
                }
            }
        }

        if (chartData.rolesChart?.categories && chartData.rolesChart?.data && chartData.rolesChart.data.length > 0) {
            analysis.hasRoleData = true;
            const maxIndex = chartData.rolesChart.data.indexOf(Math.max(...chartData.rolesChart.data));
            if (maxIndex >= 0 && chartData.rolesChart.categories[maxIndex]) {
                analysis.topRole = chartData.rolesChart.categories[maxIndex];
                analysis.topRoleCount = chartData.rolesChart.data[maxIndex].toString();
                analysis.roleAnalysis = `Rol bazlı dağılımda "${analysis.topRole}" en yüksek katılımı göstermiştir.`;
            }
        }

        if (chartData.departmentsChart?.labels && chartData.departmentsChart?.series && chartData.departmentsChart.series.length > 0) {
            analysis.hasDepartmentData = true;
            const maxIndex = chartData.departmentsChart.series.indexOf(Math.max(...chartData.departmentsChart.series));
            if (maxIndex >= 0 && chartData.departmentsChart.labels[maxIndex]) {
                analysis.topDepartment = chartData.departmentsChart.labels[maxIndex];
                analysis.topDepartmentCount = chartData.departmentsChart.series[maxIndex].toString();
                analysis.departmentAnalysis = `Departman bazlı incelemede "${analysis.topDepartment}" öne çıkmıştır.`;
            }
        }

        return analysis;
    }
}

export default new HTMLToPDFService();
